package br.com.htms.inspeccaocampo.shared.ui.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.auth.UserDisplayName
import br.com.htms.inspeccaocampo.data.AppConfig
import br.com.htms.inspeccaocampo.data.FormTipo
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/**
 * Name combo: type to filter / free-text, plus registered user suggestions.
 * Suggestions are filtered by canAccessForm(formTipo, appConfig) when formTipo is set.
 *
 * On Compose Web, avoids ExposedDropdownMenuBox and DropdownMenu (popup steals focus
 * and breaks typing/backspace). Suggestions render inline under the field.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PersonNameAutocompleteField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    isError: Boolean = false,
    supportingText: (@Composable (() -> Unit))? = null,
    formTipo: FormTipo? = null,
    appConfig: AppConfig? = null,
    suggestions: List<String>? = null
) {
    var listOpen by remember { mutableStateOf(false) }
    var loaded by remember { mutableStateOf<List<UserDisplayName>>(emptyList()) }
    val scope = rememberCoroutineScope()
    var closeJob by remember { mutableStateOf<Job?>(null) }

    LaunchedEffect(suggestions, formTipo, appConfig) {
        if (suggestions != null) {
            loaded = suggestions.map { UserDisplayName(username = it, nome = it, display = it) }
            return@LaunchedEffect
        }
        AuthApi.listUserDisplayNames()
            .onSuccess { list ->
                loaded = if (formTipo == null) {
                    list
                } else {
                    list.filter { it.canAccessForm(formTipo, appConfig) }
                }
            }
            .onFailure {
                loaded = emptyList()
            }
    }

    val displayNames = remember(loaded) {
        loaded.map { it.display }.distinct().sorted()
    }

    val filtered = remember(value, displayNames) {
        val q = value.trim()
        if (q.isEmpty()) displayNames
        else displayNames.filter { it.contains(q, ignoreCase = true) }
    }

    // Keep list open while typing; hide only on unique exact match.
    val exactOnlyMatch = filtered.size == 1 && filtered.first().equals(value.trim(), ignoreCase = true)
    val showList = listOpen && filtered.isNotEmpty() && !exactOnlyMatch

    fun openList() {
        closeJob?.cancel()
        listOpen = true
    }

    fun scheduleClose() {
        closeJob?.cancel()
        closeJob = scope.launch {
            delay(280)
            listOpen = false
        }
    }

    Column(modifier = modifier.fillMaxWidth()) {
        AppOutlinedTextField(
            value = value,
            onValueChange = { newValue ->
                onValueChange(newValue)
                openList()
            },
            label = { Text(label) },
            singleLine = true,
            isError = isError,
            supportingText = supportingText,
            trailingIcon = {
                IconButton(
                    onClick = {
                        closeJob?.cancel()
                        listOpen = !listOpen
                    },
                    modifier = Modifier.focusProperties { canFocus = false }
                ) {
                    ExposedDropdownMenuDefaults.TrailingIcon(expanded = listOpen)
                }
            },
            modifier = Modifier
                .fillMaxWidth()
                .onFocusChanged { state ->
                    if (state.isFocused) {
                        openList()
                    } else {
                        // Delay so suggestion click can register before close.
                        scheduleClose()
                    }
                },
            colors = FormFieldDefaults.textFieldColors()
        )
        if (showList) {
            Surface(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 4.dp),
                shape = MaterialTheme.shapes.small,
                tonalElevation = 2.dp,
                shadowElevation = 3.dp,
                color = MaterialTheme.colorScheme.surfaceVariant
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(max = 240.dp)
                        .verticalScroll(rememberScrollState())
                ) {
                    filtered.take(12).forEach { name ->
                        Text(
                            text = name,
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable {
                                    closeJob?.cancel()
                                    onValueChange(name)
                                    listOpen = false
                                }
                                .padding(horizontal = 12.dp, vertical = 10.dp),
                            style = MaterialTheme.typography.bodyLarge
                        )
                    }
                }
            }
        }
    }
}

/** Resolve display-name list from AuthApi. */
suspend fun loadPersonNameSuggestions(): List<UserDisplayName> =
    AuthApi.listUserDisplayNames().getOrElse { emptyList() }